import React from 'react'
import { Grid, Typography, Button, Box, Card, CardContent, useTheme } from '@mui/material'
import BrandedPageHeader from 'components/Layout/BrandedPageHeader'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import SurveyIcon from '@mui/icons-material/Assessment'
import MapIcon from '@mui/icons-material/Map'
import HomeIcon from '@mui/icons-material/Home'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'

const ResourceCard = ({ title, description, icon, link, buttonText }) => {
  const theme = useTheme()
  
  return (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography variant="h5" component="h2" sx={{ ml: 1, color: theme.palette.primary.dark }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {description}
        </Typography>
        <Button
          component={Link}
          to={link}
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          target={link.startsWith('http') ? '_blank' : undefined}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          {buttonText}
        </Button>
      </CardContent>
    </Card>
  )
}

const Resources = () => {
  const theme = useTheme()
  const signedIn = useSelector(state => state.identity.signedIn)

  const resources = [
    {
      title: "Hospital Directory",
      description: "Access our comprehensive directory of hospitals and healthcare systems. Connect directly with in-house recruiters and explore opportunities across the country with our interactive map.",
      icon: <MapIcon sx={{ fontSize: 32, color: theme.palette.primary.dark }} />,
      link: "/directory",
      buttonText: "Explore Directory"
    },
    {
      title: "Relocation Assistance",
      description: "Get expert help with your relocation needs. From finding a new home to settling into your community, our partners provide comprehensive support for medical professionals.",
      icon: <HomeIcon sx={{ fontSize: 32, color: theme.palette.primary.dark }} />,
      link: "/housing",
      buttonText: "Learn More"
    },
    {
      title: "Residency Transfer",
      description: "Looking to transfer your residency? Our platform connects residents with programs across the country, making the transfer process smoother and more transparent.",
      icon: <SwapHorizIcon sx={{ fontSize: 32, color: theme.palette.primary.dark }} />,
      link: "/residency-transfer",
      buttonText: "View Transfers"
    },
    {
      title: "Salary Transparency",
      description: "View salaries by geography and specialty to make informed career decisions. Contribute your salary data to help build a more transparent future for healthcare professionals.",
      icon: <SurveyIcon sx={{ fontSize: 32, color: theme.palette.primary.dark }} />,
      link: "/salary",
      buttonText: "View Salaries & Contribute"
    }
  ]

  return (
    <Box>
      <BrandedPageHeader>
        <Grid container spacing={0} sx={{ px: 0 }}>
          <Grid item xs={12} md={8} sx={{ pl: 0 }}>
            <Typography variant='h5' sx={{ color: 'white', fontWeight: 'bold', mb: 1, pl: 0 }}>
              Community Resources
            </Typography>
            <Typography variant='caption' sx={{
              color: 'rgba(255,255,255,0.7)',
              display: 'block',
              mb: { xs: 2, md: 0 },
              whiteSpace: 'normal',
              wordBreak: 'normal',
              pl: 0
            }}>
              Discover tools and services designed to support your medical career journey.
              From finding the right job to relocating to a new city, we're here to help.
            </Typography>
          </Grid>
        </Grid>
      </BrandedPageHeader>

      <Grid container item xs={12} justifyContent='center'>
        <Grid item xs={11}>
          <Grid container spacing={4}>
            {resources.map((resource, index) => (
              <Grid item xs={12} md={6} key={index}>
                <ResourceCard {...resource} />
              </Grid>
            ))}

            {!signedIn && (
              <Grid item xs={12}>
                <Box sx={{ 
                  mt: 6,
                  p: 4,
                  backgroundColor: theme.palette.primary.light,
                  borderRadius: 2,
                  textAlign: 'center'
                }}>
                  <Typography variant='h5' sx={{ mb: 2, color: theme.palette.primary.dark }}>
                    Help Build Our Community
                  </Typography>
                  <Typography variant='body1' sx={{ mb: 3, maxWidth: '800px', mx: 'auto' }}>
                    MyStethi is built by and for the medical community. Your contributions help create a more 
                    transparent and supportive environment for healthcare professionals. Whether it's sharing 
                    salary data, participating in our residency transfer network, or connecting with other 
                    medical professionals, every contribution makes a difference.
                  </Typography>
                  <Button
                    variant='contained'
                    size='large'
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    Join Our Community
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Resources
